import { computed, nanoid, queryDb } from "@livestore/livestore";
import type { Store } from "@livestore/livestore";

import { memoFn } from "@phosphor/utils";
import { emitDebugValue } from "#scripts/lib/dev/emitDebugValue.ts";
import type { LiveQuery } from "./LiveQuery.ts";
import { uiState$ } from "./livestore/queries.js";
import { events, tables } from "./livestore/schema.js";

// #region todo-vm-livestore
export type TodoItemVM = {
  key: string;
  text$: LiveQuery<string>;
  completed$: LiveQuery<boolean>;
  toggleCompleted: () => void;
  remove: () => void;
};

export type TodoListVM = {
  header: {
    newTodoText$: LiveQuery<string>; // !newTodoText
    updateNewTodoText: (text: string) => void;
    addTodo: () => void;
  };
  itemList: {
    items$: LiveQuery<TodoItemVM[]>; // !visibleTodos
  };
  footer: {
    incompleteCount$: LiveQuery<number>; // !incompleteCount
    currentFilter$: LiveQuery<"all" | "active" | "completed">; // !currentFilter
    showAll: () => void;
    showActive: () => void;
    showCompleted: () => void;
    clearCompleted: () => void;
  };
};
// #endregion todo-vm-livestore

const createID = (name: string) => `${name}_${nanoid(12)}`;

export function createTodoListScope(store: Store): TodoListVM {
  const currentFilter$ = computed((query) => query(uiState$).filter, { label: "filter" });
  const newTodoText$ = computed((query) => query(uiState$).newTodoText, { label: "newTodoText" });
  const createTodoItemVM = memoFn((id: string): TodoItemVM => {
    const completed$ = queryDb(() => tables.todos.select("completed").where({ id }).first({ behaviour: "error" }), {
      label: "todoItem.completed",
      deps: [id],
    });
    const text$ = queryDb(() => tables.todos.select("text").where({ id }).first({ behaviour: "error" }), {
      label: "todoItem.text",
      deps: [id],
    });
    return {
      key: id,
      completed$,
      text$,
      toggleCompleted: () =>
        store.commit(store.query(completed$) ? events.todoUncompleted({ id }) : events.todoCompleted({ id })),
      remove: () => store.commit(events.todoDeleted({ id, deletedAt: new Date() })),
    };
  });
  const visibleTodosQuery = (filter: "all" | "active" | "completed") =>
    queryDb(() => tables.todos.where({ completed: filter === "all" ? undefined : filter === "completed" }), {
      label: "visibleTodos",
      map: (rows): TodoItemVM[] => rows.map((row): TodoItemVM => createTodoItemVM(row.id)),
      deps: [filter],
    });
  const visibleTodos$ = computed(
    (query) => {
      const filter = query(currentFilter$);
      return query(visibleTodosQuery(filter));
    },
    { label: "visibleTodos" },
  );

  const incompleteCount$ = queryDb(tables.todos.count().where({ completed: false, deletedAt: null }), {
    label: "incompleteCount",
  });

  // #hide debugs
  store.subscribe(newTodoText$, (text) => {
    emitDebugValue("!newTodoText", text);
  });
  store.subscribe(currentFilter$, (filter) => {
    emitDebugValue("!currentFilter", filter);
  });
  store.subscribe(incompleteCount$, (count) => {
    emitDebugValue("!incompleteCount", count);
  });
  store.subscribe(visibleTodos$, (todos) => {
    emitDebugValue(
      "!visibleTodos",
      todos.map((t) => ({ key: t.key, text: store.query(t.text$), completed: store.query(t.completed$) })),
    );
  });
  // #endhide debugs

  return {
    header: {
      newTodoText$: computed((query) => query(newTodoText$), { label: "newTodoText.query" }),
      updateNewTodoText: (text: string) => store.commit(events.uiStateSet({ newTodoText: text })),
      addTodo: () => {
        const text = store.query(newTodoText$);
        if (text.trim()) {
          store.commit(
            events.todoCreated({ id: createID("todo"), text }),
            events.uiStateSet({ newTodoText: "" }), // update text
          );
        }
      },
    },
    itemList: {
      items$: visibleTodos$,
    },
    footer: {
      incompleteCount$,
      currentFilter$,
      showAll: () => store.commit(events.uiStateSet({ filter: "all" })),
      showActive: () => store.commit(events.uiStateSet({ filter: "active" })),
      showCompleted: () => store.commit(events.uiStateSet({ filter: "completed" })),
      clearCompleted: () => store.commit(events.todoClearedCompleted({ deletedAt: new Date() })),
    },
  };
}
